export default function AboutPage() {
  return (
    <article className="prose prose-slate max-w-none">
      <div className="rounded-2xl bg-white shadow-card border border-slate-200/70 p-8 space-y-8">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight text-rancho-ink">
            Compliance reference
          </h1>
          <p className="mt-2 text-sm text-rancho-slate">
            What this dashboard measures, the rules behind it, and what's
            expected of you as an employee, contractor, or team lead.
          </p>
        </header>

        <Section title="Why this matters">
          <p>
            Accurate, timely timesheets are how Rancho bills clients,
            tracks project burn, and runs payroll. When entries are added
            late, modified after a period closes, or simply not submitted,
            it ripples into project margins, invoicing accuracy, and
            financial reporting.
          </p>
          <p>
            <strong>About the "DCAA" name in the export:</strong> the
            dashboard reads Beebole's <em>DCAA audit-log</em> export. That
            label refers to the Defense Contract Audit Agency's standard
            timekeeping audit format - it's the most complete audit feed
            Beebole offers, with timestamps for every action on every
            entry. Rancho doesn't work on defense contracts; we just use
            this export because it's the richest data source Beebole
            provides for timesheet auditing.
          </p>
          <p>
            The standard rules embodied in that audit format are sensible
            regardless of contract type:
          </p>
          <ul>
            <li>Hours are recorded on the day they're worked (or close to it).</li>
            <li>Timesheets are submitted weekly for manager review.</li>
            <li>
              Timesheets are locked at month-end so the cost-allocation
              record cannot be retroactively altered without an audit trail.
            </li>
            <li>
              Any change made after a period closes is visible in the audit
              log and should be approved by a manager.
            </li>
          </ul>
        </Section>

        <Section title="The reporting window">
          <p>
            Every statistic on this dashboard is computed over a{" "}
            <strong>reporting window</strong> rather than the full history
            of timesheet data. The window currently begins on{" "}
            <strong>June 1, 2026</strong> - weeks and months before that
            date are not evaluated and don't affect any percentage shown.
          </p>
          <p>
            From <strong>January 2027</strong> onward, the window reverts
            to a normal calendar year-to-date: each new year starts a fresh
            window on January 1. Subtitles across the dashboard say{" "}
            <em>"since Jun 1"</em> while the June floor is in effect and{" "}
            <em>"YTD"</em> once the window is a plain calendar year again.
          </p>
        </Section>

        <Section title="The 80% benchmark">
          <p>
            Every percentage on this dashboard is measured against an{" "}
            <strong>80% target</strong>. That's the company-wide bar for
            weekly submission, month-end submission, team-lead approval, and
            hours worked: at least 80% of evaluated person-weeks (or
            person-months) should be fully compliant.
          </p>
          <p>You'll see the benchmark shown three ways:</p>
          <ul>
            <li>
              <strong>Color coding</strong> - any percentage at or above 80%
              is green; 70-79% is amber (close, but slipping); below 70% is red.
            </li>
            <li>
              <strong>Dashed line at 80%</strong> on the compliance-trend
              chart and the by-team chart.
            </li>
            <li>
              <strong>"ON TARGET" / "X% BELOW TARGET" pill</strong> on the
              overall-compliance hero card.
            </li>
          </ul>
        </Section>

        <Section title="Four compliance checks">
          <h3 className="text-lg font-bold text-rancho-ink mt-4">
            1. Weekly submission
          </h3>
          <p>
            Weeks run <strong>Sunday through Saturday</strong> everywhere
            on this dashboard, matching Beebole's week convention. Every
            employee and contractor must <strong>submit the prior
            week's timesheet by 11:59 PM Pacific on the following Monday</strong>.
            A week is "on time" when Beebole records at least one
            <code className="bg-slate-100 px-1 rounded text-xs"> submit </code>
            event whose timestamp is on or before the deadline.
          </p>
          <p>The dashboard reports four states for each (person, week):</p>
          <ul>
            <li>
              <Pill className="bg-emerald-500" /> <strong>On time</strong> - submitted before the deadline.
            </li>
            <li>
              <Pill className="bg-amber-400" /> <strong>Late</strong> - submitted, but after midnight Mon Pacific.
            </li>
            <li>
              <Pill className="bg-red-400" /> <strong>Not submitted</strong> - the week was never submitted. For
              employees this includes weeks with <em>no hours logged at
              all</em>: logging every week is required, so an empty week
              past its deadline is a miss, not an exemption.
            </li>
            <li>
              <Pill className="bg-slate-300" /> <strong>No entries</strong> - no hours logged that week.{" "}
              <strong>Contractors only</strong> - a contractor between
              engagements has nothing to log, so these weeks are excluded
              from their compliance percentages.
            </li>
          </ul>

          <h3 className="text-lg font-bold text-rancho-ink mt-6">
            2. Team-lead approval
          </h3>
          <p>
            Every <strong>Rancho-billed</strong> time entry (Beebole company
            ID 3) must be <strong>approved by someone other than the
            employee</strong> by the end of the <strong>second working day
            of the following month</strong> at 11:59 PM Pacific. April's
            Rancho-billed hours need to be approved by end-of-day on the 2nd
            working day of May.
          </p>
          <p>
            The check runs once per (person, month). A person-month passes
            only if <em>every</em> Rancho-billed entry that month has at
            least one on-time <code className="bg-slate-100 px-1 rounded text-xs">approve</code>
            event from a different user. Self-approves don't count. Non-Rancho
            companies aren't part of this check.
          </p>
          <p>
            People who logged zero Rancho-billed hours in a given month are
            excluded from that month's denominator. The most recent month is
            shown as "pending" on the chart until the deadline passes.
          </p>

          <h3 className="text-lg font-bold text-rancho-ink mt-6">
            3. Month-end submission
          </h3>
          <p>
            Every prior-month time entry must be{" "}
            <strong>submitted by 11:59 PM Pacific on the last day of the
            month</strong>. The weekly deadline is the following Monday, but
            entries that land at the very end of the month have to be in
            faster - by month-end itself - so the monthly close can happen
            on time.
          </p>
          <p>
            An entry passes when it has at least one{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">submit</code>{" "}
            action on or before the deadline. Entries added or edited{" "}
            <em>after</em> month-end are not part of this check; we don't
            enforce a freeze on the books, only a submission deadline. The
            weekly submission check still applies to any late-added entries.
          </p>
          <p>
            A month-end miss is the <strong>most severe state</strong> on
            the heatmap and shows as a red square with a white ✕. People
            who logged zero entries in a month are excluded from that
            month's denominator.
          </p>

          <h3 className="text-lg font-bold text-rancho-ink mt-6">
            4. Hours worked
          </h3>
          <p>
            Every employee must <strong>log their allotted hours each
            week</strong>. The allotment comes from their work schedule in
            Beebole: people with a <strong>custom schedule</strong> are held
            to that schedule's weekly quantity (e.g. 30 h), and everyone
            else is assumed <strong>full-time at 40 hours per week</strong>.
            <strong> Contractors are exempt</strong> - they bill what they
            work, so they're excluded from this check entirely (never a
            miss, never in the denominator).
          </p>
          <p>
            Like every other check, hours weeks run <strong>Sunday through
            Saturday</strong>, matching Beebole's week convention. All
            hours logged in that window count toward the total - including paid absences such as
            PTO. Company holidays that fall on one of the person's working
            days reduce that week's allotment by a day's worth of hours.
            Weeks where the person logged nothing at all are excluded from
            the denominator, same as the other checks.
          </p>
        </Section>

        <Section title="How overall compliance is computed">
          <p>
            A (person, week) is <strong>"fully compliant"</strong> iff all
            four of these hold:
          </p>
          <ul>
            <li>The weekly submission status is <em>on time</em>.</li>
            <li>The month-end check did not fail for that week.</li>
            <li>The team-lead-approval check for the calendar month
              the week starts in did not fail.</li>
            <li>The hours-worked check did not fail - the person logged
              their allotted hours that week (40 h full-time, or their
              custom Beebole schedule).</li>
          </ul>
          <p>
            <strong>Overall compliance</strong> percentages on the hero card
            and on the per-team chart are the share of person-weeks that are
            fully compliant out of all weeks in the reporting window
            (currently June 1, 2026 onward). Because approval is a
            per-month check, a single missed approval pulls down every week
            in that calendar month for the affected person.
          </p>
        </Section>

        <Section title="People who are excluded">
          <ul>
            <li>
              Members of administrative or non-billable teams
              (Administration, HR, Sales, Finance,
              Client - Genentech Support, and Unassigned).
            </li>
            <li>
              People who logged <strong>zero entries</strong> in a given
              month - they had nothing to log.
            </li>
          </ul>
        </Section>

        <Section title="What's expected of you">
          <h3 className="text-lg font-bold text-rancho-ink mt-4">
            Employees and contractors
          </h3>
          <ul>
            <li>Log hours daily (or at minimum, by Friday of each week).</li>
            <li>
              Click <strong>Submit</strong> in Beebole by 11:59 PM Pacific
              the following Monday.
            </li>
            <li>
              <strong>Stop editing prior-month entries after month-end.</strong>
              {" "}If a correction is needed, ask your manager.
            </li>
          </ul>

          <h3 className="text-lg font-bold text-rancho-ink mt-6">
            Team leads
          </h3>
          <ul>
            <li>
              Check this dashboard <strong>every Tuesday morning</strong> and
              follow up with anyone late or not submitted.
            </li>
            <li>
              On the <strong>first business day of each month</strong>,
              review the month-end miss list.
            </li>
            <li>
              <strong>Repeat offenders</strong> (2+ month-end misses within
              the reporting window) get escalated.
            </li>
            <li>
              <strong>Approve every Rancho-billed timesheet by the 2nd
              working day of the following month.</strong> The approval bar
              on the trend chart and the Approvals tab on the heatmap show
              who's behind.
            </li>
            <li>
              Only the explicit <strong>Approve</strong> action satisfies
              the team-lead check. Reject and lock don't count.
            </li>
          </ul>
        </Section>

        <Section title="How to read the dashboard">
          <ul>
            <li>
              <strong>KPI cards</strong> at the top show overall compliance
              across the reporting window (weekly + month-end + approval
              combined).
            </li>
            <li>
              <strong>Compliance trend</strong> - per-month bar chart with
              weekly submission %, month-end submission %, and team-lead
              approval % side by side.
            </li>
            <li>
              <strong>Compliance by team</strong> - horizontal bars
              showing each team's compliance across the reporting window,
              with an 80% benchmark line. Click a team to jump to it in
              the heatmap.
            </li>
            <li>
              <strong>Repeat offenders</strong> - people with 2+ month-end
              misses in the reporting window, split into employees and
              contractors.
            </li>
            <li>
              <strong>Timeliness heatmap</strong> - last 12 weeks per person,
              grouped by team, with three tabs:{" "}
              <em>Submission</em> (was this week's timesheet submitted on
              time, with no month-end edits), <em>Approvals</em> (did a
              team lead approve the person's Rancho-billed hours by the 2nd
              working day of the following month), and <em>Hours</em> (did
              the person log their allotted hours that week). All weeks in
              the same calendar month share the same approval state. Filter
              by name with the search box; hover a cell for details.
            </li>
            <li>
              <strong>Team breakdown</strong> - sortable table of each
              team's compliance rates and counts.
            </li>
            <li>
              The <strong>Export to Excel</strong> button on the heatmap
              downloads a CSV with the full per-person, per-week data,
              including a separate approval-status column for every week.
            </li>
          </ul>
        </Section>

        <Section title="Where the data comes from">
          <p>
            Submission and approval data comes from Beebole's DCAA
            audit-log export via the internal{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">
              report.export_DCAA
            </code>{" "}
            endpoint - one export per month per Refresh. Work schedules and
            logged hours for the hours-worked check come from Beebole's{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">
              time.get_calendar
            </code>{" "}
            service (one call per person). Timestamps are stored as UTC and
            converted to Pacific for deadline comparison.
          </p>
          <p>
            Cache is held for 24 hours; click <strong>Refresh</strong> in
            the header to force a rebuild.
          </p>
        </Section>
      </div>
    </article>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 text-sm text-rancho-ink leading-relaxed">
      <h2 className="text-xl font-bold tracking-tight text-rancho-ink">
        {title}
      </h2>
      <div className="space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_p]:my-2">
        {children}
      </div>
    </section>
  );
}

function Pill({ className }: { className: string }) {
  return (
    <span
      className={`inline-block w-4 h-3 rounded-sm align-middle mr-1 ${className}`}
    />
  );
}
